import React from "react";
import styled from "styled-components";
import { FaEnvelope, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { RotateLoader } from "@lib/components";
import { api } from "../api";

const DEFAULT_BG_IMAGE =
	"https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1200&auto=format&fit=crop&q=80";

const Shell = styled.div`
	min-height: 100vh;
	min-height: 100dvh;
	display: flex;
	overflow: hidden;
	background: #f1f4f7;
`;

const LeftPanel = styled.div`
	display: none;
	position: relative;
	overflow: hidden;

	@media (min-width: 1024px) {
		display: flex;
		width: 50%;
	}
`;

const LeftImage = styled.img`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const LeftOverlay = styled.div`
	position: absolute;
	inset: 0;
	background: rgba(15, 23, 42, 0.3);
`;

const LeftFooter = styled.div`
	position: absolute;
	bottom: 24px;
	left: 24px;
	color: white;
	font-size: 14px;
	opacity: 0.75;
	z-index: 1;
`;

const RightPanel = styled.div`
	flex: 1;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	overflow: hidden;
	padding: 0;
	background: #f1f4f7;
	min-height: 100vh;
	min-height: 100dvh;

	@media (min-width: 640px) {
		justify-content: center;
		padding: 40px 24px;
	}

	@media (min-width: 1024px) {
		justify-content: center;
	}
`;

const FrostBg = styled.div`
	pointer-events: none;
	position: absolute;
	inset: 0;
	background-image: url(${DEFAULT_BG_IMAGE});
	background-size: cover;
	background-position: center;
	transform: scale(1.05);
	filter: blur(5px);
`;

const FrostOverlay = styled.div`
	pointer-events: none;
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.60);
	backdrop-filter: blur(16px);
	-webkit-backdrop-filter: blur(8px);
`;

const Content = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 428px;
	display: flex;
	flex-direction: column;
	gap: 32px;
	padding: 40px 40px 24px;
	box-sizing: border-box;
`;

const AuthIntro = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const AuthHeading = styled.h1`
	font-size: 24px;
	font-weight: 600;
	color: #000000;
	margin: 0;
	line-height: 34px;
`;

const AuthSubheading = styled.p`
	font-size: 14px;
	font-weight: 400;
	color: #000000;
	margin: 4px 0 0;
	line-height: 24px;
`;

const FormStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
	width: 100%;
`;

const FormField = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	width: 100%;
`;

const FormLabel = styled.label`
	display: block;
	font-size: 14px;
	font-weight: 600;
	color: #4b4f5e;
	line-height: 24px;
	margin: 0;
`;

const InputWrap = styled.div`
	position: relative;
	width: 100%;
`;

const LeadingIcon = styled.span`
	pointer-events: none;
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: #6b7270;
	width: 24px;
	height: 24px;

	svg {
		width: 24px;
		height: 24px;
	}
`;

const FormInput = styled.input<{ hasTrailingIcon?: boolean }>`
	width: 100%;
	box-sizing: border-box;
	height: 52px;
	padding: 12px 12px 12px 44px;
	padding-right: ${(props) => (props.hasTrailingIcon ? "40px" : "12px")};
	font-size: 16px;
	line-height: 24px;
	color: #0f172a;
	background: #fefefe;
	border: 1px solid #dfdfdf;
	border-radius: 4px;
	transition: border-color 0.15s, box-shadow 0.15s;

	&::placeholder {
		color: #6b7270;
	}

	&:focus {
		outline: none;
		border-color: #fb2f2f;
		box-shadow: 0 0 0 2px rgba(251, 47, 47, 0.4);
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

const PasswordToggle = styled.button`
	position: absolute;
	right: 12px;
	top: 50%;
	transform: translateY(-50%);
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #6b7270;

	&:hover {
		color: #4b4f5e;
	}
`;

const LegalText = styled.p`
	margin: 0;
	font-size: 14px;
	line-height: 1.5;
	color: #4b4f5e;

	a {
		color: #fb2f2f;
		text-decoration: underline;
		font-weight: 700;
		text-underline-offset: 2px;

		&:hover {
			opacity: 0.85;
		}
	}
`;

const SubmitButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	height: 56px;
	padding: 0 16px;
	font-size: 16px;
	font-weight: 700;
	color: white;
	background: #fb2f2f;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	box-shadow: 0px 10px 18px rgba(225, 29, 72, 0.3);
	transition: opacity 0.15s;

	&:hover:not(:disabled) {
		opacity: 0.9;
	}

	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px white, 0 0 0 4px #fb2f2f, 0px 10px 18px rgba(225, 29, 72, 0.3);
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(15, 23, 42, 0.45);
`;

const ModalCard = styled.div`
	width: 100%;
	max-width: 360px;
	padding: 24px;
	border-radius: 8px;
	background: #ffffff;
	box-shadow: 0 16px 40px rgba(15, 23, 42, 0.2);
	text-align: center;
`;

const ModalTitle = styled.h2`
	margin: 0 0 8px;
	font-size: 18px;
	font-weight: 700;
	color: #0f172a;
`;

const ModalMessage = styled.p`
	margin: 0;
	font-size: 15px;
	line-height: 1.5;
	color: #475569;
`;

const SUSPENDED_MESSAGE = "Account suspended";
const SUSPENSION_MODAL_MS = 4000;

interface Props {
	setLoggedIn: (v: boolean) => void;
	masterEmail: (v: string) => void;
}

export function ViewLogin({ setLoggedIn, masterEmail }: Props) {
	const [loading, setLoading] = React.useState(false);
	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [passwordVisible, setPasswordVisible] = React.useState(false);
	const [suspensionMessage, setSuspensionMessage] = React.useState<string | null>(null);
	const suspensionTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const currentYear = new Date().getFullYear();

	React.useEffect(() => {
		return () => {
			if (suspensionTimerRef.current) {
				clearTimeout(suspensionTimerRef.current);
			}
		};
	}, []);

	const showSuspensionModal = (message: string) => {
		if (suspensionTimerRef.current) {
			clearTimeout(suspensionTimerRef.current);
		}
		setSuspensionMessage(message);
		suspensionTimerRef.current = setTimeout(() => {
			setSuspensionMessage(null);
			suspensionTimerRef.current = null;
		}, SUSPENSION_MODAL_MS);
	};

	return (
		<Shell>
			<LeftPanel>
				<LeftImage src={DEFAULT_BG_IMAGE} alt="" />
				<LeftOverlay />
				<LeftFooter>&copy; {currentYear} Ordering Dashboard</LeftFooter>
			</LeftPanel>

			<RightPanel>
				<FrostBg aria-hidden="true" />
				<FrostOverlay aria-hidden="true" />
				<Content>
					<AuthIntro>
						<AuthHeading>Welcome Back</AuthHeading>
						<AuthSubheading>Sign in to your account to continue.</AuthSubheading>
					</AuthIntro>

					<form
						onSubmit={async (e) => {
							e.preventDefault();
							try {
								setLoading(true);
								const result = await api.login({ email, password });
								if (result.outcome) {
									if (result.message === SUSPENDED_MESSAGE) {
										showSuspensionModal(result.message);
									} else {
										alert(result.message);
									}
								} else {
									api.token = result.token;
									masterEmail(email);
									setLoggedIn(true);
								}
							} catch (err) {
								console.log(err);
								alert("Something went wrong, please try again or contact us");
							} finally {
								setLoading(false);
							}
						}}
					>
						<FormStack>
							<FormField>
								<FormLabel htmlFor="email">Email Address</FormLabel>
								<InputWrap>
									<LeadingIcon aria-hidden="true">
										<FaEnvelope />
									</LeadingIcon>
									<FormInput
										id="email"
										type="email"
										name="email"
										value={email}
										placeholder="you@example.com"
										onChange={(e) => setEmail(e.target.value)}
										autoComplete="email"
										required
										disabled={loading}
									/>
								</InputWrap>
							</FormField>

							<FormField>
								<FormLabel htmlFor="password">Password</FormLabel>
								<InputWrap>
									<LeadingIcon aria-hidden="true">
										<FaLock />
									</LeadingIcon>
									<FormInput
										id="password"
										type={passwordVisible ? "text" : "password"}
										name="password"
										value={password}
										placeholder="Enter your password"
										onChange={(e) => setPassword(e.target.value)}
										autoComplete="current-password"
										required
										disabled={loading}
										hasTrailingIcon
									/>
									<PasswordToggle
										type="button"
										onClick={() => setPasswordVisible(!passwordVisible)}
										aria-label={passwordVisible ? "Hide password" : "Show password"}
									>
										{passwordVisible ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
									</PasswordToggle>
								</InputWrap>
							</FormField>

							<LegalText>
								By logging in, you acknowledge that you remain legally bound by the Reseller Agreement for your account — including these{" "}
								<a href="/termsandconditions" target="_blank" rel="noopener noreferrer">
									Terms and Conditions
								</a>
								; any user-specific terms, and the{" "}
								<a href="/privacypolicy" target="_blank" rel="noopener noreferrer">
									Privacy Policy
								</a>
								.
							</LegalText>

							<SubmitButton type="submit" disabled={loading}>
								{loading ? <RotateLoader size={2} /> : "Login"}
							</SubmitButton>
						</FormStack>
					</form>
				</Content>
			</RightPanel>

			{suspensionMessage && (
				<ModalOverlay role="alertdialog" aria-modal="true" aria-labelledby="suspension-modal-title">
					<ModalCard>
						<ModalTitle id="suspension-modal-title">Account Suspended</ModalTitle>
						<ModalMessage>{suspensionMessage}</ModalMessage>
					</ModalCard>
				</ModalOverlay>
			)}
		</Shell>
	);
}
